/**
 * ClosedAccountModal — show payment details and actions.
 * Props: open, onClose, payment, orders, tableId
 */
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, FileText, AlertCircle } from "lucide-react";
import { formatPrice } from "@/utils/currency";

const METHOD_MAP = { cash: "Dinheiro", dinheiro: "Dinheiro", efectivo: "Dinheiro", card: "Cartão", credit_card: "Cartão", mbway: "MBWay", bizum: "Bizum", pix: "Pix", stripe: "Stripe", viva: "Viva", revolut: "Revolut" };
const methodLabel = (m) => METHOD_MAP[(m || "").toLowerCase()] || m || "—";
const fmtDate = (iso) => !iso ? "—" : new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtTime = (iso) => !iso ? "—" : new Date(iso).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });

export function ClosedAccountModal({ open, onClose, payment, orders, tableId }) {
  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [nifInput, setNifInput] = useState("");
  const [showNifStep, setShowNifStep] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (!open) {
      setInvoiceId(null);
      setInvoiceNumber(null);
      setPdfUrl(null);
      setNifInput("");
      setShowNifStep(false);
      setActionError("");
      return;
    }
    if (payment?.vendus_invoice_id) {
      setInvoiceId(payment.vendus_invoice_id);
      setInvoiceNumber(payment.vendus_invoice_number);
      setPdfUrl(payment.vendus_invoice_pdf_url);
    } else {
      setInvoiceId(null);
      setInvoiceNumber(null);
      setPdfUrl(null);
    }
    setNifInput("");
    setShowNifStep(false);
    setActionError("");
  }, [open, payment?.id]);

  if (!payment) return null;

  const allItems = orders
    .filter((o) => o.status !== "cancelled")
    .flatMap((o) => (o.items || []).map((it) => ({
      product_name: it.product_name || it.name || "",
      quantity: it.quantity || it.qty || 1,
      unit_price: it.unit_price || it.price || 0,
    })));

  const handlePrintReceipt = async () => {
    setLoading(true);
    setActionError("");
    try {
      await authAxios.post(`/tables/${tableId}/history/payments/${payment.id}/print-receipt`);
    } catch (err) {
      setActionError(err.response?.data?.detail || "Erro ao reimprimir recibo");
    } finally {
      setLoading(false);
    }
  };

  const validateNif = (nif) => {
    if (nif === "") return true;
    return /^\d{9}$/.test(nif);
  };

  const handleCreateInvoice = async (step2 = false) => {
    if (!step2) {
      setShowNifStep(true);
      return;
    }

    if (!validateNif(nifInput)) {
      setActionError("NIF: vazio, ou exatamente 9 dígitos");
      return;
    }

    setLoading(true);
    setActionError("");
    try {
      const res = await authAxios.post(`/vendus/table-payment/${payment.id}/invoice`, { customer_nif: nifInput });
      if (res.data.success || res.data.existing) {
        setInvoiceId(res.data.invoice_id);
        setInvoiceNumber(res.data.invoice_number);
        setPdfUrl(res.data.pdf_url);
        setShowNifStep(false);
        setNifInput("");
      } else {
        setActionError(res.data.error || "Erro ao criar fatura");
      }
    } catch (err) {
      setActionError(err.response?.data?.detail || "Erro ao criar fatura");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInvoice = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    } else {
      setActionError("Fatura sem PDF disponível");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Conta Fechada</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Payment info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Data</span>
              <span className="font-medium">
                {fmtDate(payment.created_at)} às{" "}
                {fmtTime(payment.created_at)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Forma de Pagamento</span>
              <span className="font-medium">
                {methodLabel(payment.method)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Responsável</span>
              <span className="font-medium">
                {payment.created_by || "Sistema"}
              </span>
            </div>
            {payment.discount_applied > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Desconto</span>
                <span className="font-medium text-red-600">
                  -{formatPrice(payment.discount_applied)}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-lg text-emerald-700">
                {formatPrice(payment.amount)}
              </span>
            </div>
          </div>

          {/* Items table */}
          {allItems.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">Itens</h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-400 uppercase tracking-wide">
                    <th className="text-left pb-1 font-medium">Item</th>
                    <th className="text-center pb-1 font-medium w-8">Qtd</th>
                    <th className="text-right pb-1 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {allItems.map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="py-1 text-gray-700">
                        {it.product_name || "—"}
                      </td>
                      <td className="py-1 text-center text-gray-500">
                        {it.quantity}
                      </td>
                      <td className="py-1 text-right text-gray-700 font-medium">
                        {formatPrice(it.quantity * it.unit_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Error */}
          {actionError && (
            <div className="bg-red-50 border border-red-200 rounded p-3 flex gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{actionError}</p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-2">
            <Button
              onClick={handlePrintReceipt}
              disabled={loading}
              className="w-full gap-2"
              variant="outline"
            >
              <Printer className="w-4 h-4" />
              Reimprimir Conta
            </Button>

            {invoiceId ? (
              <Button onClick={handleOpenInvoice} disabled={loading} className="w-full gap-2" variant="default">
                <FileText className="w-4 h-4" />
                Abrir / Reimprimir Nota
              </Button>
            ) : showNifStep ? (
              <div className="space-y-2">
                <label className="text-sm text-gray-700 block">NIF (opcional, 9 dígitos ou vazio)</label>
                <input
                  type="text"
                  maxLength={9}
                  placeholder="123456789"
                  value={nifInput}
                  onChange={(e) => setNifInput(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-xs text-gray-500">Deixar vazio para consumidor final</p>
                <div className="flex gap-2">
                  <Button onClick={() => { setShowNifStep(false); setNifInput(""); }} disabled={loading} className="flex-1" variant="outline">Cancelar</Button>
                  <Button onClick={() => handleCreateInvoice(true)} disabled={loading} className="flex-1 gap-2" variant="default">
                    <FileText className="w-4 h-4" />
                    Confirmar
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => handleCreateInvoice(false)} disabled={loading} className="w-full gap-2" variant="default">
                <FileText className="w-4 h-4" />
                Emitir Nota Fiscal
              </Button>
            )}
          </div>

          {invoiceId && (
            <div className="text-xs text-gray-500 italic">
              ✓ Nota fiscal: {invoiceNumber || invoiceId}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
